// Global Instructions Rule Applied!
//
// My Drafts — MY WORKSPACE → the drafts agents wrote for me (assigned to me
// as an account owner, or unassigned on an account I own). Owner decision 11
// of 2026-09-02: a plain menu item, always visible; an empty list is harmless.
// Chrome copies the My Agents page (title row + body), cards are the shared
// AgentDraftCard grouped by account.
import React, { useMemo, useState } from 'react'
import { Switch, Typography } from 'antd'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { HelpButton } from '../../../core/components/HelpDrawer/HelpButton.tsx'
import { dismissErrorToast, showErrorToast } from '../../../core/utils/errorToast.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'
import { AgentDraftRecord } from '../../../state_manager/data_models/agent_drafts_model.ts'
import { useAgentDrafts } from '../hooks/useAgentDrafts.ts'
import {
  confirmAgentDraftCandidate, exportAgentDraftToOutlook, fetchAgentDraftInteractionOptions, markAgentDraftSent, setAgentDraftStatus
} from '../../internal_agents/services/agentDraftsService.ts'
import { AgentDraftCard } from '../../internal_agents/components/AgentDraftCard.tsx'
import { HELP_ARTICLES } from '../../../ui_configs/helpArticles.ts'
import { accountFormPath } from '../utils/openRecord.ts'

const { Title, Text } = Typography

export default function MyDraftsPage() {
  const navigate = useNavigate()
  const [showHistory, setShowHistory] = useState(false)
  const { drafts, loading, refetch, outlookCapability } = useAgentDrafts({ mine: true }, { includeHistory: showHistory })
  const [busyId, setBusyId] = useState<string | null>(null)
  const activateRef = useRefreshOnActivate(refetch)

  const groups = useMemo(() => {
    const map = new Map<string, { name: string; drafts: AgentDraftRecord[] }>()
    for (const d of drafts) {
      const g = map.get(d.agent_drafts__account_id) ?? { name: d.accounts__name, drafts: [] }
      g.drafts.push(d)
      map.set(d.agent_drafts__account_id, g)
    }
    return Array.from(map.entries())
  }, [drafts])

  // Keyed error toast per draft: persists until closed or the same action
  // later succeeds (FRONTEND_DESIGN_PATTERNS "Feedback on a blocked action").
  const act = async (draft: AgentDraftRecord, fn: () => Promise<void>, done: string) => {
    const key = `agent-draft-${draft.agent_drafts__id}`
    setBusyId(draft.agent_drafts__id)
    try {
      await fn()
      dismissErrorToast(key)
      toast.success(done)
      await refetch()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Action failed', { key })
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div ref={activateRef} data-component='MyDraftsPage' className='h-full overflow-hidden'>
      <div className='w-full h-full flex flex-col bg-white px-4 py-3 overflow-hidden'>
        <div className='flex items-center gap-2' style={{ margin: '0 0 8px 0' }}>
          <Title level={4} style={{ margin: 0 }}>
            <FontAwesomeIcon icon={faPenToSquare} style={{ marginRight: 8 }} />
            My Drafts
          </Title>
          {/* Page-level help (Phase 6c): the guide covers the whole draft
              lifecycle, not one section of the page, and the drawer is
              non-modal so the list behind it stays usable. Same slug as the
              Relationship tab section's "?" — one guide, two surfaces. */}
          <HelpButton articleKey={HELP_ARTICLES.agentDrafts} variant='title' />
          <span className='ml-auto inline-flex items-center gap-1 text-xs text-gray-600'>
            <Switch size='small' checked={showHistory} onChange={setShowHistory} /> Show history
          </span>
        </div>
        <Text type='secondary' style={{ marginBottom: 8 }}>
          Suggestions your agents drafted for the LPs you cover. Reservoir never sends them —{' '}
          {outlookCapability.enabled
            ? 'send one to your Outlook Drafts folder or copy it, then edit and send it yourself. A draft in Outlook turns Sent once your email reaches Reservoir.'
            : 'copy the text, edit, and send it yourself.'}
        </Text>
        <div className='flex-1 min-h-0 overflow-auto flex flex-col gap-2'>
          {!loading && groups.length === 0 ? (
            <FormSection title='Drafts' titleColor='blue'>
              <div className='text-center text-gray-500 text-sm py-4'>Nothing waiting for you.</div>
            </FormSection>
          ) : null}
          {groups.map(([accountId, g]) => (
            <FormSection
              key={accountId}
              title={g.name}
              titleColor='blue'
              headerActions={
                <button type='button' className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-sm' onClick={() => navigate(accountFormPath(accountId))}>
                  Open
                </button>
              }
            >
              <div className='flex flex-col gap-2'>
                {g.drafts.map((d) => (
                  <AgentDraftCard
                    key={d.agent_drafts__id}
                    draft={d}
                    hideAccount
                    busy={busyId === d.agent_drafts__id}
                    outlookEnabled={outlookCapability.enabled}
                    onAccept={(draft, action) => act(draft, () => setAgentDraftStatus(draft.agent_drafts__id, 'accepted', getAccessToken(store.getState().auth), { acceptedAction: action }), 'Draft copied')}
                    onDismiss={(draft, reason) => act(draft, () => setAgentDraftStatus(draft.agent_drafts__id, 'dismissed', getAccessToken(store.getState().auth), { reason }), 'Draft dismissed')}
                    onExportOutlook={(draft) => act(draft, async () => { await exportAgentDraftToOutlook(draft.agent_drafts__id, getAccessToken(store.getState().auth)) }, 'Draft created in your Outlook Drafts folder')}
                    onConfirmCandidate={(draft, accept) => act(draft, async () => { await confirmAgentDraftCandidate(draft.agent_drafts__id, accept, getAccessToken(store.getState().auth)) }, accept ? 'Draft marked as sent' : 'Candidate dismissed')}
                    onMarkSent={(draft, interactionId) => act(draft, async () => { await markAgentDraftSent(draft.agent_drafts__id, interactionId, getAccessToken(store.getState().auth)) }, 'Draft marked as sent')}
                    loadInteractions={(draft) => fetchAgentDraftInteractionOptions(draft.agent_drafts__account_id, getAccessToken(store.getState().auth))}
                  />
                ))}
              </div>
            </FormSection>
          ))}
        </div>
      </div>
    </div>
  )
}
